import React, { useMemo, useRef, useState, useEffect, useCallback } from 'react';
import moment from 'moment';
import { normalizeStatus } from './executionHistoryUtils';
import WorkflowService from '../../services/api/workflow';
import { FaRoute } from "react-icons/fa";
import { useSearchParams } from 'react-router-dom';
import ReactFlow, { Background, Controls, MiniMap } from 'reactflow';
import 'reactflow/dist/style.css';
import { proOptions, nodeTypes, edgeTypes } from './workflowConstants';
import { formatDuration } from '../../settings/shared/commonMethods';
const asArray = (v) => (Array.isArray(v) ? v : []);
const tableCellEllipsisStyle = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap'
};

const wfStatusPillStyle = (raw) => {
  const s = String(raw || '').trim();
  const upper = s.toUpperCase();
  const key = upper || 'UNKNOWN';

  const stylesByKey = {
    RUNNING: { color: '#b45309', bg: '#fff4d6' },
    FAILED: { color: '#be123c', bg: '#ffecef' },
    ERROR: { color: '#be123c', bg: '#ffecef' },
    SUCCESS: { color: '#047857', bg: '#eafff5' },
    COMPLETED: { color: '#047857', bg: '#eafff5' }
  };

  const { color, bg } = stylesByKey[key] || { color: '#2563eb', bg: '#eef2ff' };
  return {
    label: key,
    style: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '6px 14px',
      borderRadius: '999px',
      fontWeight: 800,
      fontSize: 10,
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
      color,
      backgroundColor: bg
    }
  };
};

const buildTraceViewModel = (trace) => {
  const asNumberOrNull = (v) => {
    if (v == null || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const nodes = asArray(trace?.nodes);
  const edges = asArray(trace?.edges);

  const llmConfigNodes = nodes.filter(
    (n) => n?.type === 'config' && n?.data?.configType === 'llm'
  );
  const llmTypeToModel = new Map(
    llmConfigNodes
      .map((n) => [
        n?.data?.llmType,
        n?.data?.model || n?.data?.displayName || n?.data?.llmType
      ])
      .filter(([k, v]) => Boolean(k) && Boolean(v))
  );

  const models = [
    ...new Set(
      [
        ...asArray(trace?.appConfig?.llm).map(
          (l) => l?.llmConfig?.model || l?.title
        ),
        ...llmConfigNodes.map(
          (n) => n?.data?.model || n?.data?.displayName || n?.data?.llmType
        )
      ].filter(Boolean)
    )
  ];

  const handlerConfigMap = new Map();
  asArray(trace?.appConfig?.handlerConfig).forEach((handler) => {
    if (handler?.handlerName) {
      handlerConfigMap.set(handler.handlerName, handler);
    }
  });

  const agents = nodes
    .filter((n) => n?.type === 'agent' || n?.type === 'task')
    .map((n) => {
      const title = n?.data?.title || n?.title || n?.data?.label || n?.id;
      const llmType = n?.data?.llm;
      const llmLabel = llmTypeToModel.get(llmType) || llmType;

      // Get tools from connectedConfig or tools array
      const toolNames =
        asArray(n?.data?.connectedConfig?.tool).filter(Boolean).length > 0
          ? asArray(n?.data?.connectedConfig?.tool).filter(Boolean)
          : asArray(n?.data?.tools).filter(Boolean);

      const tools = toolNames.map((toolName) => {
        const handlerConfig = handlerConfigMap.get(toolName) || 
          Array.from(handlerConfigMap.values()).find(
            (h) => h?.title === toolName || h?.displayName === toolName
          );
        
        return {
          name: toolName,
          handlerName: handlerConfig?.handlerName || toolName,
          attributes: handlerConfig?.attribute || null
        };
      });

      return {
        name: title,
        type: n?.type,
        llms: llmLabel ? [llmLabel] : [],
        tools
      };
    });

  const displayName =
    trace?.appConfig?.appName || 'Trace';

  const createdAt = trace?.createdDate || trace?.updatedDate || null;

  const allExecMsgs = [
    ...asArray(trace?.execMsgDTOS),
    ...asArray(trace?.workFlowExecMsgDTOS?.content)
  ];

  let status = trace?.active === true ? 'running' : 'success';
  if (allExecMsgs && allExecMsgs.length > 0) {
    const hasError = allExecMsgs.some((m) => {
      const ev = String(m?.event || '').toLowerCase();
      return ev === 'agent_execute_error' || ev === 'request_failed';
    });
    const hasComplete = allExecMsgs.some((m) => {
      const evUpper = String(m?.event || '').toUpperCase();
      const evLower = String(m?.event || '').toLowerCase();
      return (
        evUpper === 'EVENT_AGENT_EXECUTE_COMPLETE' ||
        evUpper === 'AGENT_EXECUTE_COMPLETE' ||
        evLower === 'agent_execute_complete'
      );
    });

    if (hasError) status = 'error';
    else if (hasComplete) status = 'success';
    else status = 'running';
  }

  return {
    ...trace,
    name: displayName,
    action: trace?.action || '—',
    status,
    wfStatus: trace?.wfStatus ?? null,
    totalTokens:asNumberOrNull(trace?.totalTokens) || null,
    model: models.join(', '),
    models,
    agents,
    nodes,
    edges,
    nodeCount: nodes.length,
    edgeCount: edges.length,
    timestamp: createdAt || new Date().toISOString()
  };
};

const parseWorkflowHistoryResponse = (response, pageSize) => {
  let list = [];
  let total = 0;
  let pages = 0;

  if (Array.isArray(response)) {
    list = response;
    total = response.length;
    pages = Math.ceil(total / pageSize);
  } else if (response?.content && typeof response.totalElements === 'number') {
    list = response.content || [];
    total = response.totalElements || 0;
    pages = response.totalPages || 0;
  } else if (response?.workFlowTemplateDTOPage) {
    list = response.workFlowTemplateDTOPage.content || [];
    total = response.workFlowTemplateDTOPage.totalElements || 0;
    pages = response.workFlowTemplateDTOPage.totalPages || 0;
  } else if (response?.data) {
    list = Array.isArray(response.data) ? response.data : [];
    total = list.length;
    pages = Math.ceil(total / pageSize);
  }

  return {
    traces: asArray(list).map(buildTraceViewModel),
    totalElements: total,
    totalPages: pages
  };
};

const TracesView = ({
  projectId,
  projectName: projectNameProp,
  historyData,
  currentPage: currentPageProp,
  pageSize: pageSizeProp,
  setCurrentPage: setCurrentPageProp,
  setPageSize: setPageSizeProp,
  onViewWorkflowDiagram,
  onViewExecutionHistory,
  onClose
}) => {
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [modelFilter, setModelFilter] = useState('All Models');
  const [viewMode, setViewMode] = useState('table'); 
  const [selectedTrace, setSelectedTrace] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const didAutoOpenExecutionRef = useRef(false);
  // Pagination state (controlled by parent when provided)
  const [currentPageInternal, setCurrentPageInternal] = useState(0);
  const [pageSizeInternal, setPageSizeInternal] = useState(5);
  const currentPage = typeof currentPageProp === 'number' ? currentPageProp : currentPageInternal;
  const pageSize = typeof pageSizeProp === 'number' ? pageSizeProp : pageSizeInternal;
  const setCurrentPage = typeof setCurrentPageProp === 'function' ? setCurrentPageProp : setCurrentPageInternal;
  const setPageSize = typeof setPageSizeProp === 'function' ? setPageSizeProp : setPageSizeInternal;
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);

  const projectName =
    traces?.[0]?.appConfig?.appName ||
    'Untitled Project';

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('force-close-console-drawer'));
  }, []);

  const loadTraces = useCallback(async ({ silent = false } = {}) => {
    if (!projectId) {
      setTraces([]);
      setTotalPages(0);
      setTotalElements(0);
      return;
    }

    if (!silent) setLoading(true);
    try {
      const response = await WorkflowService.getWorkflowHistory(projectId, currentPage, pageSize);
      const { traces: nextTraces, totalElements, totalPages: nextTotalPages } =
        parseWorkflowHistoryResponse(response, pageSize);
      setTraces(nextTraces);
      setTotalElements(totalElements);
      setTotalPages(nextTotalPages);
    } catch (e) {
      setTraces([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [projectId, currentPage, pageSize]);

  useEffect(() => {
    loadTraces();
  }, [loadTraces]);

  useEffect(() => {
    const handleWorkflowStatusMessage = (event) => {
      const message = event?.detail;
      if (!message || !projectId) return;

      const messageProjectId =
        message?.ninfo?.projectId || message?.projectId || message?.project_id;
      if (messageProjectId && String(messageProjectId) !== String(projectId)) return;

      loadTraces({ silent: true });
    };

    window.addEventListener('workflowStatusMessageReceived', handleWorkflowStatusMessage);
    return () => {
      window.removeEventListener('workflowStatusMessageReceived', handleWorkflowStatusMessage);
    };
  }, [projectId, loadTraces]);

  useEffect(() => {
    const executionIdFromUrl = searchParams.get('executionId');
    if (!executionIdFromUrl) return;
    if (didAutoOpenExecutionRef.current) return;
    if (!Array.isArray(traces) || traces.length === 0) return;
    if (typeof onViewExecutionHistory !== 'function') return;

    const match = traces.find((t) => {
      const eid = t?.executionId ?? t?.workflowExecutionId ?? t?.id ?? null;
      return eid != null && String(eid) === String(executionIdFromUrl);
    });
    if (!match) return;
    didAutoOpenExecutionRef.current = true;
    onViewExecutionHistory(match);
  }, [traces, searchParams, onViewExecutionHistory]);

  useEffect(() => {
    didAutoOpenExecutionRef.current = false;
  }, [projectId]);

  const formatTime = (timestamp) => {
    return moment(timestamp).fromNow();
  };

  const filteredTraces = traces.filter(trace => {
    const matchesSearch =
      (trace?.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (trace?.id || '').toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All Status' || trace.status === statusFilter.toLowerCase();
    const matchesModel =
      modelFilter === 'All Models' ||
      asArray(trace?.models).some((m) => m === modelFilter) ||
      trace.model === modelFilter;
    return matchesSearch && matchesStatus && matchesModel;
  });

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: '#ffffff'
    }}>
   
      {/* Content Area */}
      <div style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        // overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '15px 10px 0px 15px',
          borderBottom: '1px solid #e5e7eb',
          backgroundColor: '#ffffff'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '8px'
          }}>
            <div>
              <h1 style={{
                margin: 0,
                fontSize: '20px',
                fontWeight: 700,
                color: '#111827',
                marginBottom: '4px'
              }}>
                <FaRoute style={{ fontSize: '20px', marginRight: '8px' , color: '#FF5722'}} />
                <span style={{ marginLeft: '8px' }}>Traces</span>
              </h1>
              <p style={{
                margin: 0,
                fontSize: '14px',
                color: '#6b7280'
              }}>
                View and analyze agent execution
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                padding: '8px',
                border: 'none',
                borderRadius: '8px',
                backgroundColor: 'transparent',
                color: '#6b7280',
                fontSize: '20px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '36px',
                height: '36px'
              }}
              title="Close"
              aria-label="Close"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f3f4f6';
                e.currentTarget.style.color = '#111827';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
                e.currentTarget.style.color = '#6b7280';
              }}
            >
              <i className="fa-solid fa-xmark" />
            </button>
          </div>
      </div>

        {/* Table Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '0 32px 32px'
        }}>
        {loading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#6b7280'
          }}>
            Loading traces...
          </div>
        ) : filteredTraces.length === 0 ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: '#6b7280'
          }}>
            No traces found
          </div>
        ) : (
          /* Wrapper div now owns the outer border/radius so it survives
             fractional-zoom + border-collapse rendering issues */
          <div style={{
            overflowX: 'auto',
            marginTop: '24px',
            border: '1px solid #e5e7eb',
            borderRadius: '12px'
          }}>
          <table style={{
            width: '100%',
            minWidth: '1320px',
            tableLayout: 'fixed',
            borderCollapse: 'collapse'
          }}>
            <colgroup>
              <col style={{ width: '20%' }} /> {/* NAME */}
              <col style={{ width: '10%' }} />  {/* TOTAL TOKENS */}
              <col style={{ width: '10%' }} /> {/* DURATION */}
              <col style={{ width: '12%' }} /> {/* LAST EXECUTED */}
              <col style={{ width: '16%' }} /> {/* CREATED DATE */}
              <col style={{ width: '10%' }} /> {/* CREATED BY */}
              <col style={{ width: '11%' }} /> {/* STATUS */}
              <col style={{ width: '8%' }} />  {/* DIAGRAM */}
              <col style={{ width: '3%' }} />  {/* CHEVRON */}
            </colgroup>
            <thead>
              <tr style={{
                borderBottom: `1px solid #e5e7eb`,
                background: 'linear-gradient(90deg, #3b82f614 0%, #8b5cf614 50%, #f8fafc 100%)',
                borderRadius: '12px'
              }}>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...tableCellEllipsisStyle
                }}>
                  NAME
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...tableCellEllipsisStyle
                }}>
                  TOTAL TOKENS
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...tableCellEllipsisStyle
                }}>
                  DURATION
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...tableCellEllipsisStyle
                }}>
                  LAST EXECUTED
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...tableCellEllipsisStyle
                }}>
                  CREATED DATE
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...tableCellEllipsisStyle
                }}>
                  CREATED BY
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...tableCellEllipsisStyle
                }}>
                  STATUS
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...tableCellEllipsisStyle
                }}>
                  DIAGRAM
                </th>
                <th style={{
                  padding: '12px 16px',
                  textAlign: 'left',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#6b7280',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  ...tableCellEllipsisStyle
                }}>
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredTraces.map((trace) => (
                <tr
                  key={trace.id}
                  style={{
                    borderBottom: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s'
                  }}
                  onClick={() => {
                    const executionId =
                      trace?.executionId ||
                      trace?.workflowExecutionId ||
                      trace?.id ||
                      null;
                    if (executionId != null) {
                      setSearchParams((prev) => {
                        const next = new URLSearchParams(prev);
                        next.set('executionId', String(executionId));
                        return next;
                      });
                    }
                    onViewExecutionHistory?.(trace);
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#f9fafb';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }}
                >
                  {/* NAME */}
                  <td style={{ padding: '16px', ...tableCellEllipsisStyle }}>
                    <div style={{ fontWeight: 500, color: '#111827', marginBottom: '4px', ...tableCellEllipsisStyle }}>
                      {trace.name}
                    </div>
                    {trace.agents && trace.agents.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#3b82f6', marginTop: '4px', ...tableCellEllipsisStyle }}>
                        <i className="fa-solid fa-robot" style={{ fontSize: '10px', marginRight: '4px' }}></i>
                        {trace.agents.map(a => typeof a === 'string' ? a : a.name).join(', ')}
                      </div>
                    )}
                  </td>

                  {/* TOTAL TOKENS */}
                  <td style={{
                    padding: '16px',
                    color: '#6b7280',
                    fontSize: '14px',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                    ...tableCellEllipsisStyle
                  }}>
                    {trace?.totalTokens != null ? trace.totalTokens : '—'}
                  </td>

                  {/* DURATION */}
                  <td style={{ padding: '16px', color: '#6b7280', fontSize: '14px', ...tableCellEllipsisStyle }}>
                    {typeof trace?.duration?.durationInMillis === 'number' ? (
                      <>
                        {formatDuration(trace.duration.durationInMillis)}
                      </>
                    ) : (
                      '-'
                    )}
                  </td>

                  {/* LAST EXECUTED */}
                  <td style={{ padding: '16px', color: '#6b7280', fontSize: '14px', ...tableCellEllipsisStyle }}>
                    {formatTime(trace.timestamp)}
                  </td>

                  {/* CREATED DATE */}
                  <td style={{ padding: '16px', color: '#6b7280', fontSize: '14px', ...tableCellEllipsisStyle }}>
                    {trace.createdDate != null
                      ? `${moment.utc(trace.createdDate).local().format('MMM DD, YYYY')} ${moment
                          .utc(trace.createdDate)
                          .local()
                          .format('hh:mm A')}`
                      : ''}
                  </td>

                  {/* CREATED BY */}
                  <td style={{ padding: '12px', color: '#6b7280', ...tableCellEllipsisStyle }}>
                   {trace.createdBy}
                  </td>

                  {/* STATUS — this cell was previously missing, causing the column shift */}
                  <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                    {trace?.wfStatus ? (
                      <span {...(() => {
                        const p = wfStatusPillStyle(trace.wfStatus);
                        return { style: p.style, children: p.label };
                      })()} />
                    ) : (
                      <span style={{ color: '#9ca3af' }}>—</span>
                    )}
                  </td>

                  {/* DIAGRAM */}
                  <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedTrace(trace);
                        onViewWorkflowDiagram?.(trace, historyData);
                      }}
                      style={{
                        padding: '6px 12px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        backgroundColor: '#ffffff',
                        color: '#374151',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f9fafb';
                        e.currentTarget.style.borderColor = '#d1d5db';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '#ffffff';
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }}
                      title="View workflow diagram"
                    >
                      <i className="fa-solid fa-diagram-project" style={{
                        fontSize: '12px',
                        color: '#3b82f6'
                      }}></i>
                      Diagram
                    </button>
                  </td>

                  {/* CHEVRON */}
                  <td style={{ padding: '16px', verticalAlign: 'middle' }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onViewExecutionHistory?.(trace);
                      }}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                      title="View execution history"
                    >
                      <i className="fa-solid fa-chevron-right" style={{
                        color: '#9ca3af',
                        fontSize: '12px',
                        transition: 'all 0.2s'
                      }}></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}

        {/* Pagination Controls */}
        {!loading && traces.length > 0 && totalElements > 0 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 0',
            borderTop: '1px solid #e5e7eb',
            marginTop: '24px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              <span>Showing</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(0); 
                }}
                style={{
                  padding: '6px 10px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  fontSize: '14px',
                  backgroundColor: '#ffffff',
                  cursor: 'pointer',
                  outline: 'none'
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>of {totalElements} traces</span>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <button
                onClick={() => setCurrentPage(0)}
                disabled={currentPage === 0}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  backgroundColor: currentPage === 0 ? '#f3f4f6' : '#ffffff',
                  color: currentPage === 0 ? '#9ca3af' : '#374151',
                  fontSize: '14px',
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="First page"
              >
                <i className="fa-solid fa-angles-left" style={{ fontSize: '12px' }}></i>
              </button>
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  backgroundColor: currentPage === 0 ? '#f3f4f6' : '#ffffff',
                  color: currentPage === 0 ? '#9ca3af' : '#374151',
                  fontSize: '14px',
                  cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="Previous page"
              >
                <i className="fa-solid fa-chevron-left" style={{ fontSize: '12px' }}></i>
              </button>
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                padding: '0 8px'
              }}>
                <span style={{
                  fontSize: '14px',
                  color: '#374151',
                  fontWeight: 500
                }}>
                  Page {currentPage + 1} of {totalPages || 1}
                </span>
              </div>

              <button
                onClick={() => setCurrentPage(Math.min(totalPages - 1, currentPage + 1))}
                disabled={currentPage >= totalPages - 1}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  backgroundColor: currentPage >= totalPages - 1 ? '#f3f4f6' : '#ffffff',
                  color: currentPage >= totalPages - 1 ? '#9ca3af' : '#374151',
                  fontSize: '14px',
                  cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="Next page"
              >
                <i className="fa-solid fa-chevron-right" style={{ fontSize: '12px' }}></i>
              </button>
              <button
                onClick={() => setCurrentPage(totalPages - 1)}
                disabled={currentPage >= totalPages - 1}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  backgroundColor: currentPage >= totalPages - 1 ? '#f3f4f6' : '#ffffff',
                  color: currentPage >= totalPages - 1 ? '#9ca3af' : '#374151',
                  fontSize: '14px',
                  cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title="Last page"
              >
                <i className="fa-solid fa-angles-right" style={{ fontSize: '12px' }}></i>
              </button>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Trace Detail Modal */}
      {selectedTrace && (
        <TraceDetailModal
          trace={selectedTrace}
          onClose={() => setSelectedTrace(null)}
        />
      )}
    </div>
  );
};

// Trace Detail Modal Component
const TraceDetailModal = ({ trace, onClose }) => {
  const normalizedStatus = normalizeStatus(trace?.status);
  const statusColor =
    normalizedStatus === 'success'
      ? '#10b981'
      : normalizedStatus === 'error'
        ? '#ef4444'
        : normalizedStatus === 'running'
          ? '#f59e0b'
          : '#6b7280';
  const statusText =
    normalizedStatus === 'success'
      ? 'Success'
      : normalizedStatus === 'error'
        ? 'Failed'
        : normalizedStatus === 'running'
          ? 'Running'
          : (trace?.status ? String(trace.status) : 'Status');

  const { nodes, edges } = useMemo(() => {
    const rawNodes = asArray(trace?.nodes);
    const rawEdges = asArray(trace?.edges);

    const nodesOut = rawNodes.map((node) => ({
      id: node.id,
      type: node.type || 'task',
      position: node.position || { x: 0, y: 0 },
      data: node.data || { label: node.label || node.name || node.id },
      ...node
    }));

    const edgesOut = rawEdges.map((edge) => {
      if (edge.source && edge.target) {
        return {
          id: edge.id || `${edge.source}-${edge.target}`,
          source: edge.source,
          target: edge.target,
          type: edge.type || 'custom',
          label: edge.label || edge.data?.label,
          ...edge
        };
      }
      if (edge.from && edge.to) {
        return {
          id: edge.id || `${edge.from}-${edge.to}`,
          source: edge.from,
          target: edge.to,
          type: edge.type || 'custom',
          label: edge.label || edge.data?.label,
          ...edge
        };
      }
      return edge;
    });

    return { nodes: nodesOut, edges: edgesOut };
  }, [trace]);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 3000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: '1400px',
          height: '90vh',
          backgroundColor: '#ffffff',
          borderRadius: '12px',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 32px',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{
              margin: 0,
              fontSize: '24px',
              fontWeight: 700,
              color: '#111827',
              marginBottom: '4px'
            }}>
              {trace.name}
            </h2>
          </div>
          <button
            onClick={onClose}
            style={{
              padding: '8px',
              border: 'none',
              borderRadius: '8px',
              backgroundColor: 'transparent',
              color: '#6b7280',
              fontSize: '20px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f3f4f6';
              e.currentTarget.style.color = '#111827';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#6b7280';
            }}
          >
            <i className="fa-solid fa-xmark"></i>
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          padding: '0',
          overflow: 'hidden'
        }}>
          <style>{`
            /* Remove ReactFlow default selection outline/box */
            .react-flow__node.selected,
            .reactflow__node.selected {
              outline: none !important;
              box-shadow: none !important;
            }

            /* Ensure wrapper nodes don't render a background "box" */
            .react-flow__node-default,
            .react-flow__node-input,
            .react-flow__node-output,
            .react-flow__node-group {
              border: none !important;
              padding: 0 !important;
              background: transparent !important;
              background-color: transparent !important;
              box-shadow: none !important;
            }
          `}</style>
          {nodes.length > 0 ? (
            <div style={{ width: '100%', height: '100%' }}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                edgeTypes={edgeTypes}
                fitView
                minZoom={0.5}
                maxZoom={1}
                proOptions={proOptions}
                style={{ width: '100%', height: '100%', backgroundColor: '#f9fafb' }}
              >
                <Background />
                <Controls position="bottom-left" />
                <MiniMap position="bottom-right" />
              </ReactFlow>
            </div>
          ) : (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280', fontSize: '14px' }}>
              No workflow diagram available
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TracesView;